/**
 * RequireAdmin — route guard for admin-only pages (v5.5).
 *
 * Behavior:
 *  - Reads the auth user (via /api/auth/me) on mount.
 *  - If not authenticated → redirect to /admin/login carrying `?next=...`.
 *  - If authenticated but not owner/admin → friendly forbidden screen.
 *  - While loading → minimal "Checking access..." panel.
 */
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";

export default function RequireAdmin({ children }) {
  const loc = useLocation();
  const [state, setState] = useState({ status: "loading", user: null });

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!on) return;
        const u = data?.user || data;
        if (u && (u.role === "owner" || u.role === "admin")) {
          setState({ status: "ok", user: u });
        } else if (u) {
          setState({ status: "forbidden", user: u });
        } else {
          setState({ status: "unauthenticated", user: null });
        }
      } catch {
        if (on) setState({ status: "unauthenticated", user: null });
      }
    })();
    return () => {
      on = false;
    };
  }, [loc.pathname]);

  if (state.status === "loading") {
    return (
      <div className="ssc-container py-16" data-testid="require-admin-loading">
        <div className="rounded-2xl border border-[var(--ssc-line)] bg-white p-6 text-center text-[var(--ssc-ink-2)]">
          Checking access...
        </div>
      </div>
    );
  }

  if (state.status === "unauthenticated") {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/admin/login?next=${next}`} replace />;
  }

  if (state.status === "forbidden") {
    return (
      <div className="ssc-container py-16" data-testid="require-admin-forbidden">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="font-extrabold text-amber-900">
            Admin access required
          </h2>
          <p className="mt-2 text-amber-800 text-sm">
            You&apos;re signed in, but this page is restricted to dealership
            admins. If you think this is a mistake, please contact Scott.
          </p>
        </div>
      </div>
    );
  }

  return children;
}
